import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { loanAPI } from '../../services/api';
import { getUnreadNotificationCount } from '../../utils/studentInAppNotifications';
import StudentSidebar from './StudentSidebar';
import './StudentShell.css';

const DRAWER_BREAKPOINT = 1280;

const StudentShell: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [useDrawer, setUseDrawer] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useLayoutEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DRAWER_BREAKPOINT + 1}px)`);
    const update = () => {
      const isLargeDesktop = mql.matches;
      setUseDrawer(!isLargeDesktop);
      setSidebarCollapsed(!isLargeDesktop);
    };
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await loanAPI.getStudentPayments();
        if (!cancelled && res.success && res.data) {
          setUnreadCount(getUnreadNotificationCount(res.data.loans || [], res.data.payments || []));
        }
      } catch {
        /* ignore badge errors */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const closeDrawer = () => setSidebarCollapsed(true);

  return (
    <div className="student-shell">
      <StudentSidebar
        currentPath={location.pathname}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
        isMobileDrawer={useDrawer}
      />

      <div className={`student-shell-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="student-shell-header">
          <div className="student-shell-header-left">
            <button
              type="button"
              className="student-shell-toggle"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              aria-label={sidebarCollapsed ? 'Open menu' : 'Close menu'}
            >
              <span aria-hidden>☰</span>
            </button>
          </div>
          <div className="student-shell-header-right">
            <button
              type="button"
              className="student-shell-bell"
              onClick={() => navigate('/dashboard/notifications')}
              aria-label="Notifications"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && <span className="student-shell-badge">{unreadCount}</span>}
            </button>
            <button
              type="button"
              className="student-shell-account"
              onClick={() => navigate('/dashboard/profile')}
            >
              <span className="student-shell-avatar">{(user?.first_name || 'S').charAt(0).toUpperCase()}</span>
              <span className="student-shell-name">{user?.first_name || 'Profile'}</span>
            </button>
          </div>
        </header>

        <div className="student-shell-content">
          <Outlet />
        </div>
      </div>

      {useDrawer && !sidebarCollapsed && (
        <div
          className="student-shell-overlay"
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={closeDrawer}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') closeDrawer();
          }}
        />
      )}
    </div>
  );
};

export default StudentShell;
